import { useState, useEffect, Fragment } from 'react'
import { getUsageSummary, getUsageDetails, getUsageFilterOptions, getRecentPrompts, getPromptDetail } from '@/api/settings'
import type { UsageFilters, PromptLogEntry, PromptLogDetail } from '@/api/settings'
import { formatCredits } from '@/api/billing'

/**
 * What this account's AI has consumed — the detail behind the credit balance.
 *
 * Everything here is denominated in CREDITS, the unit the platform charges in.
 * Provider dollars are deliberately absent: the usage endpoints strip them, and
 * the only place they appear is the instance-admin console, where an operator
 * needs to compare what a user was billed against what it cost to serve.
 */
export default function UsageDashboard() {
  const [summary, setSummary] = useState<any>(null)
  const [details, setDetails] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [detailDays, setDetailDays] = useState(30)

  // Filter options (loaded once)
  const [filterOptions, setFilterOptions] = useState<{ projects: string[]; task_types: string[]; obj_names: string[] }>({ projects: [], task_types: [], obj_names: [] })

  // Active filters
  const [selProjects, setSelProjects] = useState<string[]>([])
  const [selTaskTypes, setSelTaskTypes] = useState<string[]>([])
  const [selObjNames, setSelObjNames] = useState<string[]>([])
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')

  // Recent Ask-AI prompts (prompt-level log) + drill-down
  const [prompts, setPrompts] = useState<PromptLogEntry[]>([])
  const [expandedPrompt, setExpandedPrompt] = useState<string | null>(null)
  const [promptDetail, setPromptDetail] = useState<PromptLogDetail | null>(null)

  // Load filter options once
  useEffect(() => {
    getUsageFilterOptions().then(setFilterOptions).catch(() => {})
  }, [])

  // Build filters object
  const buildFilters = (): UsageFilters => {
    const f: UsageFilters = {}
    if (!dateFrom && !dateTo) f.days = detailDays
    if (selProjects.length) f.projects = selProjects
    if (selTaskTypes.length) f.task_types = selTaskTypes
    if (selObjNames.length) f.obj_names = selObjNames
    if (dateFrom) f.date_from = dateFrom
    if (dateTo) f.date_to = dateTo
    return f
  }

  // Fetch data when filters change
  useEffect(() => {
    setLoading(true)
    // Recent prompts honor the same project + window filters. The prompts
    // endpoint takes a single project, so only narrow when exactly one is
    // selected; otherwise show all.
    const promptProject = selProjects.length === 1 ? selProjects[0] : undefined
    Promise.all([
      getUsageSummary(),
      getUsageDetails(buildFilters()),
      getRecentPrompts({ project: promptProject, days: detailDays, limit: 100 }),
    ]).then(([s, d, p]) => {
      setSummary(s)
      setDetails(d)
      setPrompts(p)
    }).catch(() => {}).finally(() => setLoading(false))
    // Collapse any open drill-down when filters change.
    setExpandedPrompt(null)
    setPromptDetail(null)
  }, [detailDays, selProjects, selTaskTypes, selObjNames, dateFrom, dateTo])

  const togglePromptDetail = (promptId: string) => {
    if (expandedPrompt === promptId) {
      setExpandedPrompt(null)
      setPromptDetail(null)
      return
    }
    setExpandedPrompt(promptId)
    setPromptDetail(null)
    getPromptDetail(promptId).then(setPromptDetail).catch(() => {})
  }

  const clearFilters = () => {
    setSelProjects([])
    setSelTaskTypes([])
    setSelObjNames([])
    setDateFrom('')
    setDateTo('')
    setDetailDays(30)
  }

  const hasActiveFilters = selProjects.length > 0 || selTaskTypes.length > 0 || selObjNames.length > 0 || dateFrom || dateTo

  if (loading && !details) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem' }}>
        <span className="spinner" style={{ width: 24, height: 24, borderWidth: 2 }} />
      </div>
    )
  }

  if (!summary) return <p style={{ color: 'var(--color-text-secondary)' }}>No usage data available.</p>

  const limits = summary.limits || {}
  const monthly = summary.monthly || {}
  const today = summary.today || {}
  // Platform-key usage is capped by the plan limits; own-key usage is unlimited.
  const platform = summary.platform || { input_tokens: 0, output_tokens: 0, credits: 0, calls: 0 }
  const own = summary.own || { input_tokens: 0, output_tokens: 0, credits: 0, calls: 0 }

  const inputPct = limits.monthly_input_tokens > 0 ? Math.min((platform.input_tokens / limits.monthly_input_tokens) * 100, 100) : 0
  const outputPct = limits.monthly_output_tokens > 0 ? Math.min((platform.output_tokens / limits.monthly_output_tokens) * 100, 100) : 0
  const creditsPct = limits.monthly_credits > 0 ? Math.min((platform.credits / limits.monthly_credits) * 100, 100) : 0

  const formatTokens = (n: number) => n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 1_000 ? `${(n / 1_000).toFixed(0)}K` : String(n)

  const totals = details?.totals || { input_tokens: 0, output_tokens: 0, credits: 0, charged: 0, calls: 0 }

  // Whose key funds this account's own-key calls. Resolved server-side from the
  // current LLM settings, because a usage row records only THAT it ran on a
  // supplied key, never whose. See usage_service.resolve_own_key_owner.
  const ownIsOrg = (details?.own_key_owner || summary.own_key_owner) === 'organisation'
  const ownKeyTitle = ownIsOrg ? 'Your Organisation\u2019s Key' : 'Your Own Key'
  // Credits the window's usage was worth, versus what actually left the wallet.
  const notCharged = Math.max((totals.credits || 0) - (totals.charged || 0), 0)

  return (
    <>
      {/* Plan & Meters */}
      <section style={{
        background: 'var(--color-bg-secondary)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-lg)',
        padding: '1.5rem',
        marginBottom: '1.5rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '0.25rem' }}>
          <span className="material-icons" style={{ fontSize: 21, color: 'var(--color-accent)' }}>speed</span>
          <h2 style={{ fontSize: '1.125rem', fontWeight: 600, margin: 0 }}>
            Platform-key Usage — {summary.plan_label} Plan
          </h2>
        </div>
        <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '0 0 1.25rem 28px' }}>
          Your monthly allowance on the shared platform key. Switch to your own API key for unlimited usage.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.25rem', marginBottom: '1rem' }}>
          <UsageMeter label="Input Tokens" value={platform.input_tokens} limit={limits.monthly_input_tokens} pct={inputPct} format={formatTokens} />
          <UsageMeter label="Output Tokens" value={platform.output_tokens} limit={limits.monthly_output_tokens} pct={outputPct} format={formatTokens} />
          <UsageMeter label="Credits" value={platform.credits} limit={limits.monthly_credits} pct={creditsPct} format={formatCredits} />
        </div>

        <div style={{ display: 'flex', gap: '2rem', fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
          <span>Today: {formatTokens(today.input_tokens)} in / {formatTokens(today.output_tokens)} out / {formatCredits(today.credits)} credits / {today.calls} calls</span>
          <span>This month: {monthly.calls} total calls</span>
        </div>
      </section>

      {/* Own-key usage — not subject to any limit */}
      <section style={{
        background: 'var(--color-bg-secondary)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-lg)',
        padding: '1.5rem',
        marginBottom: '1.5rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '0.25rem' }}>
          <span className="material-icons" style={{ fontSize: 21, color: 'var(--color-accent)' }}>key</span>
          <h2 style={{ fontSize: '1.125rem', fontWeight: 600, margin: 0 }}>{ownKeyTitle} — No Limit</h2>
        </div>
        <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '0 0 1.25rem 28px' }}>
          {ownIsOrg
            ? 'Your account has no API key of its own, so these calls ran on your organisation\u2019s shared key. Not counted against your plan allowance, and never charged to your credit balance.'
            : 'Usage billed to your own API key this month. Not counted against your plan allowance, and never charged to your credit balance.'}
        </p>
        <div style={{ display: 'flex', gap: '2.5rem', flexWrap: 'wrap', fontSize: '0.9375rem' }}>
          <div><div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>Input Tokens</div><strong>{formatTokens(own.input_tokens)}</strong></div>
          <div><div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>Output Tokens</div><strong>{formatTokens(own.output_tokens)}</strong></div>
          <div>
            <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>Would Have Cost</div>
            <strong>{formatCredits(own.credits)}</strong>
            <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}> credits</span>
          </div>
          <div><div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>Credits Charged</div><strong>{formatCredits(own.charged ?? 0)}</strong></div>
          <div><div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>Calls</div><strong>{own.calls}</strong></div>
        </div>
      </section>

      {/* Filters */}
      <section style={{
        background: 'var(--color-bg-secondary)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-lg)',
        padding: '1rem 1.5rem',
        marginBottom: '1.5rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '0.75rem' }}>
          <span className="material-icons" style={{ fontSize: 19, color: 'var(--color-text-secondary)' }}>filter_list</span>
          <span style={{ fontSize: '0.9375rem', fontWeight: 600 }}>Filters</span>
          {hasActiveFilters && (
            <button className="btn btn-sm btn-ghost" onClick={clearFilters} style={{ marginLeft: 'auto', fontSize: '0.8125rem', gap: 4 }}>
              <span className="material-icons" style={{ fontSize: 15 }}>clear</span>
              Clear All
            </button>
          )}
          {loading && <span className="spinner" style={{ width: 14, height: 14, borderWidth: 2, marginLeft: hasActiveFilters ? 0 : 'auto' }} />}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
          <MultiSelect
            label="Project"
            options={filterOptions.projects}
            selected={selProjects}
            onChange={setSelProjects}
          />
          <MultiSelect
            label="Task Type"
            options={filterOptions.task_types}
            selected={selTaskTypes}
            onChange={setSelTaskTypes}
          />
          <MultiSelect
            label="Object"
            options={filterOptions.obj_names}
            selected={selObjNames}
            onChange={setSelObjNames}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <label style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              style={{
                background: 'var(--color-bg-primary)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
                padding: '4px 8px',
                fontSize: '0.8125rem',
                color: 'var(--color-text-primary)',
                colorScheme: 'dark',
              }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <label style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>To</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              style={{
                background: 'var(--color-bg-primary)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
                padding: '4px 8px',
                fontSize: '0.8125rem',
                color: 'var(--color-text-primary)',
                colorScheme: 'dark',
              }}
            />
          </div>
          {!dateFrom && !dateTo && (
            <div style={{ display: 'flex', gap: '0.35rem', marginLeft: '0.5rem' }}>
              {[7, 14, 30, 60].map((d) => (
                <button
                  key={d}
                  className={`btn btn-sm ${d === detailDays ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => setDetailDays(d)}
                  style={{ padding: '2px 10px', fontSize: '0.8125rem' }}
                >
                  {d}d
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Totals Banner */}
      {details && (
        <section style={{
          background: 'var(--color-bg-secondary)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-lg)',
          padding: '1rem 1.5rem',
          marginBottom: '1.5rem',
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 1fr)',
          gap: '1rem',
        }}>
          <div>
            <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginBottom: 2 }}>Total Calls</div>
            <div style={{ fontSize: '1.1875rem', fontWeight: 700 }}>{totals.calls.toLocaleString()}</div>
          </div>
          <div>
            <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginBottom: 2 }}>Input Tokens</div>
            <div style={{ fontSize: '1.1875rem', fontWeight: 700 }}>{formatTokens(totals.input_tokens)}</div>
          </div>
          <div>
            <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginBottom: 2 }}>Output Tokens</div>
            <div style={{ fontSize: '1.1875rem', fontWeight: 700 }}>{formatTokens(totals.output_tokens)}</div>
          </div>
          <div title="What this usage is worth in credits, whether or not it was charged to you.">
            <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginBottom: 2 }}>Credits Used</div>
            <div style={{ fontSize: '1.1875rem', fontWeight: 700 }}>{formatCredits(totals.credits)}</div>
          </div>
          <div title="What actually came out of your credit balance.">
            <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginBottom: 2 }}>Charged to Balance</div>
            <div style={{ fontSize: '1.1875rem', fontWeight: 700, color: 'var(--color-accent)' }}>{formatCredits(totals.charged ?? 0)}</div>
          </div>
        </section>
      )}

      {details && (
        <>
          {/* Usage by Source — WHICH KEY PAID, and how much of it actually
              left the credit balance. First breakdown on purpose: a credit
              figure means nothing until you know whether it was charged. */}
          {details.by_source?.length > 0 && (
            <section style={{
              background: 'var(--color-bg-secondary)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-lg)',
              padding: '1.5rem',
              marginBottom: '1.5rem',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '0.25rem' }}>
                <span className="material-icons" style={{ fontSize: 19, color: 'var(--color-accent)' }}>account_balance_wallet</span>
                <h3 style={{ fontSize: '0.9375rem', fontWeight: 600, margin: 0 }}>Usage by Source</h3>
              </div>
              <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '0 0 1rem 26px' }}>
                Which key paid for each call. Only calls served by the platform key
                are charged to your credit balance — usage on a key you or your
                organisation supplied is billed directly by the provider.
              </p>
              <table style={{ width: '100%', fontSize: '0.875rem', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600 }}>Funding source</th>
                    <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600 }}>Calls</th>
                    <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600 }}>Input</th>
                    <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600 }}>Output</th>
                    <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600 }} title="What this usage is worth in credits.">Credits</th>
                    <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600 }} title="What actually came out of your credit balance.">Charged</th>
                  </tr>
                </thead>
                <tbody>
                  {details.by_source.map((r: any) => (
                    <tr key={r.source_key} style={{ borderBottom: '1px solid var(--color-border)' }}>
                      <td style={{ padding: '6px 8px' }}>
                        <SourceBadge sourceKey={r.source_key} label={r.source_label} />
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>{r.calls}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>{formatTokens(r.input_tokens)}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>{formatTokens(r.output_tokens)}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>{formatCredits(r.credits)}</td>
                      <td style={{
                        padding: '6px 8px',
                        textAlign: 'right',
                        fontWeight: r.charged > 0 ? 600 : 400,
                        color: r.charged > 0 ? 'var(--color-accent)' : 'var(--color-text-secondary)',
                      }}>
                        {formatCredits(r.charged)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {notCharged > 0 && (
                <div style={{
                  display: 'flex',
                  gap: 8,
                  marginTop: '0.875rem',
                  padding: '0.625rem 0.75rem',
                  background: 'var(--color-bg-tertiary, rgba(127,127,127,0.08))',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: '0.8125rem',
                  color: 'var(--color-text-secondary)',
                }}>
                  <span className="material-icons" style={{ fontSize: 17, flexShrink: 0 }}>info</span>
                  <span>
                    <strong>{formatCredits(notCharged)} credits</strong> of usage in this window
                    was <strong>not</strong> charged to your balance
                    {ownIsOrg
                      ? ' — these calls ran on your organisation\u2019s own key, so the provider bills your organisation directly.'
                      : ' — these calls ran on a key you supplied, so the provider bills you directly.'}
                  </span>
                </div>
              )}
            </section>
          )}

          {/* Daily Trend */}
          {details.daily?.length > 0 && (
            <section style={{
              background: 'var(--color-bg-secondary)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-lg)',
              padding: '1.5rem',
              marginBottom: '1.5rem',
            }}>
              <h3 style={{ fontSize: '0.9375rem', fontWeight: 600, marginBottom: '1rem' }}>Daily Credit Usage</h3>
              <DailyChart data={details.daily} />
            </section>
          )}

          {/* By Task Type */}
          {details.by_task?.length > 0 && (
            <section style={{
              background: 'var(--color-bg-secondary)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-lg)',
              padding: '1.5rem',
              marginBottom: '1.5rem',
            }}>
              <h3 style={{ fontSize: '0.9375rem', fontWeight: 600, marginBottom: '1rem' }}>Usage by Task Type</h3>
              <table style={{ width: '100%', fontSize: '0.875rem', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600 }}>Task</th>
                    <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600 }}>Calls</th>
                    <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600 }}>Input</th>
                    <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600 }}>Output</th>
                    <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600 }}>Credits</th>
                  </tr>
                </thead>
                <tbody>
                  {details.by_task.map((r: any) => (
                    <tr key={r.task_type} style={{ borderBottom: '1px solid var(--color-border)' }}>
                      <td style={{ padding: '6px 8px' }}>{r.task_type}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>{r.calls}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>{formatTokens(r.input_tokens)}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>{formatTokens(r.output_tokens)}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>{formatCredits(r.credits)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {/* By Model */}
          {details.by_model?.length > 0 && (
            <section style={{
              background: 'var(--color-bg-secondary)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-lg)',
              padding: '1.5rem',
              marginBottom: '1.5rem',
            }}>
              <h3 style={{ fontSize: '0.9375rem', fontWeight: 600, marginBottom: '1rem' }}>Usage by Model</h3>
              <table style={{ width: '100%', fontSize: '0.875rem', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600 }}>Model</th>
                    <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600 }}>Provider</th>
                    <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600 }}>Calls</th>
                    <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600 }}>Input</th>
                    <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600 }}>Output</th>
                    <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600 }}>Credits</th>
                  </tr>
                </thead>
                <tbody>
                  {details.by_model.map((r: any, i: number) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--color-border)' }}>
                      <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: '0.8125rem' }}>{r.model}</td>
                      <td style={{ padding: '6px 8px' }}>{r.provider}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>{r.calls}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>{formatTokens(r.input_tokens)}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>{formatTokens(r.output_tokens)}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>{formatCredits(r.credits)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {/* By Project */}
          {details.by_project?.length > 0 && (
            <section style={{
              background: 'var(--color-bg-secondary)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-lg)',
              padding: '1.5rem',
              marginBottom: '1.5rem',
            }}>
              <h3 style={{ fontSize: '0.9375rem', fontWeight: 600, marginBottom: '1rem' }}>Usage by Project</h3>
              <table style={{ width: '100%', fontSize: '0.875rem', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600 }}>Project</th>
                    <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600 }}>Calls</th>
                    <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600 }}>Input</th>
                    <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600 }}>Output</th>
                    <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600 }}>Credits</th>
                  </tr>
                </thead>
                <tbody>
                  {details.by_project.map((r: any) => (
                    <tr key={r.project} style={{ borderBottom: '1px solid var(--color-border)' }}>
                      <td style={{ padding: '6px 8px' }}>{r.project}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>{r.calls}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>{formatTokens(r.input_tokens)}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>{formatTokens(r.output_tokens)}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>{formatCredits(r.credits)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {/* By Object */}
          {details.by_object?.length > 0 && (
            <section style={{
              background: 'var(--color-bg-secondary)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-lg)',
              padding: '1.5rem',
              marginBottom: '1.5rem',
            }}>
              <h3 style={{ fontSize: '0.9375rem', fontWeight: 600, marginBottom: '1rem' }}>Usage by Object</h3>
              <table style={{ width: '100%', fontSize: '0.875rem', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600 }}>Object</th>
                    <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600 }}>Task</th>
                    <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600 }}>Calls</th>
                    <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600 }}>Input</th>
                    <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600 }}>Output</th>
                    <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600 }}>Credits</th>
                  </tr>
                </thead>
                <tbody>
                  {details.by_object.map((r: any, i: number) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--color-border)' }}>
                      <td style={{ padding: '6px 8px' }}>{r.obj_name}</td>
                      <td style={{ padding: '6px 8px' }}>{r.task_type}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>{r.calls}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>{formatTokens(r.input_tokens)}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>{formatTokens(r.output_tokens)}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>{formatCredits(r.credits)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {/* Recent Ask-AI Prompts (prompt-level log) */}
          {prompts.length > 0 && (
            <section style={{
              background: 'var(--color-bg-secondary)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-lg)',
              padding: '1.5rem',
              marginBottom: '1.5rem',
            }}>
              <h3 style={{ fontSize: '0.9375rem', fontWeight: 600, marginBottom: '0.25rem' }}>Recent Ask-AI Prompts</h3>
              <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginBottom: '1rem' }}>
                One row per prompt you sent to the Ask-AI agent. Click a row to see the full prompt, response, and the calls it made.
              </p>
              <div style={{ maxHeight: 460, overflow: 'auto' }}>
                <table style={{ width: '100%', fontSize: '0.8125rem', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--color-border)', position: 'sticky', top: 0, background: 'var(--color-bg-secondary)' }}>
                      <th style={{ textAlign: 'left', padding: '4px 6px' }}>Time</th>
                      <th style={{ textAlign: 'left', padding: '4px 6px' }}>Project</th>
                      <th style={{ textAlign: 'left', padding: '4px 6px' }}>Agent</th>
                      <th style={{ textAlign: 'left', padding: '4px 6px', minWidth: 200 }}>Prompt</th>
                      <th style={{ textAlign: 'left', padding: '4px 6px' }}>Model</th>
                      <th style={{ textAlign: 'left', padding: '4px 6px' }}>Think</th>
                      <th style={{ textAlign: 'right', padding: '4px 6px' }}>In</th>
                      <th style={{ textAlign: 'right', padding: '4px 6px' }}>Out</th>
                      <th style={{ textAlign: 'right', padding: '4px 6px' }}>Credits</th>
                      <th style={{ textAlign: 'right', padding: '4px 6px' }}>Time</th>
                      <th style={{ textAlign: 'left', padding: '4px 6px' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prompts.map((p) => {
                      const open = expandedPrompt === p.prompt_id
                      const statusColor = p.status === 'error' ? 'var(--color-error, #e5484d)'
                        : p.status === 'running' ? 'var(--color-text-secondary)'
                        : 'var(--color-success, #30a46c)'
                      return (
                        <Fragment key={p.prompt_id}>
                          <tr
                            onClick={() => togglePromptDetail(p.prompt_id)}
                            style={{ borderBottom: '1px solid var(--color-border)', cursor: 'pointer', background: open ? 'var(--color-bg-tertiary, rgba(127,127,127,0.08))' : undefined }}
                          >
                            <td style={{ padding: '4px 6px', whiteSpace: 'nowrap' }}>
                              {p.created_at ? new Date(p.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                            </td>
                            <td style={{ padding: '4px 6px' }}>{p.project || '-'}</td>
                            <td style={{ padding: '4px 6px' }}>{p.backend}</td>
                            <td style={{ padding: '4px 6px', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.prompt_text}>
                              {p.prompt_text}
                            </td>
                            <td style={{ padding: '4px 6px', fontFamily: 'monospace' }}>{p.model || '-'}</td>
                            <td style={{ padding: '4px 6px' }}>{p.thinking_level || '-'}</td>
                            <td style={{ padding: '4px 6px', textAlign: 'right' }}>{formatTokens(p.input_tokens)}</td>
                            <td style={{ padding: '4px 6px', textAlign: 'right' }}>{formatTokens(p.output_tokens)}</td>
                            <td style={{ padding: '4px 6px', textAlign: 'right' }}>{formatCredits(p.credits)}</td>
                            <td style={{ padding: '4px 6px', textAlign: 'right' }}>{p.duration_ms ? `${(p.duration_ms / 1000).toFixed(1)}s` : '-'}</td>
                            <td style={{ padding: '4px 6px', color: statusColor }}>{p.status}</td>
                          </tr>
                          {open && (
                            <tr>
                              <td colSpan={11} style={{ padding: '0.75rem 1rem', background: 'var(--color-bg-tertiary, rgba(127,127,127,0.06))', borderBottom: '1px solid var(--color-border)' }}>
                                {!promptDetail ? (
                                  <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>Loading…</div>
                                ) : (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                    <div>
                                      <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 2 }}>Prompt</div>
                                      <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.8125rem', fontFamily: 'inherit' }}>{promptDetail.prompt_text}</pre>
                                    </div>
                                    {promptDetail.response_text && (
                                      <div>
                                        <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 2 }}>Response</div>
                                        <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.8125rem', fontFamily: 'inherit', maxHeight: 260, overflow: 'auto' }}>{promptDetail.response_text}</pre>
                                      </div>
                                    )}
                                    {promptDetail.call_breakdown?.length > 0 && (
                                      <div>
                                        <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 4 }}>
                                          Linked calls ({promptDetail.call_breakdown.length})
                                        </div>
                                        <table style={{ width: '100%', fontSize: '0.8125rem', borderCollapse: 'collapse' }}>
                                          <thead>
                                            <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                                              <th style={{ textAlign: 'left', padding: '2px 6px' }}>Task</th>
                                              <th style={{ textAlign: 'left', padding: '2px 6px' }}>Object</th>
                                              <th style={{ textAlign: 'left', padding: '2px 6px' }}>Model</th>
                                              <th style={{ textAlign: 'right', padding: '2px 6px' }}>In</th>
                                              <th style={{ textAlign: 'right', padding: '2px 6px' }}>Out</th>
                                              <th style={{ textAlign: 'right', padding: '2px 6px' }}>Credits</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {promptDetail.call_breakdown.map((c) => (
                                              <tr key={c.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                                                <td style={{ padding: '2px 6px' }}>{c.task_type}</td>
                                                <td style={{ padding: '2px 6px' }}>{c.obj_name || '-'}</td>
                                                <td style={{ padding: '2px 6px', fontFamily: 'monospace' }}>{c.model}</td>
                                                <td style={{ padding: '2px 6px', textAlign: 'right' }}>{formatTokens(c.input_tokens)}</td>
                                                <td style={{ padding: '2px 6px', textAlign: 'right' }}>{formatTokens(c.output_tokens)}</td>
                                                <td style={{ padding: '2px 6px', textAlign: 'right' }}>{formatCredits(c.credits)}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* Recent Calls */}
          {details.recent?.length > 0 && (
            <section style={{
              background: 'var(--color-bg-secondary)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-lg)',
              padding: '1.5rem',
              marginBottom: '1.5rem',
            }}>
              <h3 style={{ fontSize: '0.9375rem', fontWeight: 600, marginBottom: '1rem' }}>Recent Calls</h3>
              <div style={{ maxHeight: 400, overflow: 'auto' }}>
                <table style={{ width: '100%', fontSize: '0.8125rem', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--color-border)', position: 'sticky', top: 0, background: 'var(--color-bg-secondary)' }}>
                      <th style={{ textAlign: 'left', padding: '4px 6px' }}>Time</th>
                      <th style={{ textAlign: 'left', padding: '4px 6px' }}>Task</th>
                      <th style={{ textAlign: 'left', padding: '4px 6px' }}>Object</th>
                      <th style={{ textAlign: 'left', padding: '4px 6px' }}>Model</th>
                      <th style={{ textAlign: 'left', padding: '4px 6px' }}>Source</th>
                      <th style={{ textAlign: 'right', padding: '4px 6px' }}>In</th>
                      <th style={{ textAlign: 'right', padding: '4px 6px' }}>Out</th>
                      <th style={{ textAlign: 'right', padding: '4px 6px' }} title="What this call is worth in credits.">Credits</th>
                      <th style={{ textAlign: 'right', padding: '4px 6px' }} title="What actually came out of your credit balance.">Charged</th>
                      <th style={{ textAlign: 'right', padding: '4px 6px' }}>Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {details.recent.map((r: any) => (
                      <tr key={r.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                        <td style={{ padding: '4px 6px', whiteSpace: 'nowrap' }}>
                          {r.created_at ? new Date(r.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                        </td>
                        <td style={{ padding: '4px 6px' }}>{r.task_type}</td>
                        <td style={{ padding: '4px 6px' }}>{r.obj_name || '-'}</td>
                        <td style={{ padding: '4px 6px', fontFamily: 'monospace' }}>{r.model}</td>
                        <td style={{ padding: '4px 6px' }}>
                          <SourceBadge sourceKey={r.source_key} label={r.source_label} />
                        </td>
                        <td style={{ padding: '4px 6px', textAlign: 'right' }}>{formatTokens(r.input_tokens)}</td>
                        <td style={{ padding: '4px 6px', textAlign: 'right' }}>{formatTokens(r.output_tokens)}</td>
                        <td style={{ padding: '4px 6px', textAlign: 'right' }}>{formatCredits(r.credits)}</td>
                        <td style={{
                          padding: '4px 6px',
                          textAlign: 'right',
                          color: r.charged > 0 ? 'var(--color-accent)' : 'var(--color-text-secondary)',
                        }}>
                          {formatCredits(r.charged ?? 0)}
                        </td>
                        <td style={{ padding: '4px 6px', textAlign: 'right' }}>{r.duration_ms ? `${(r.duration_ms / 1000).toFixed(1)}s` : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </>
      )}
    </>
  )
}

/* ── Funding-source badge ─────────────────────────────────────────── */

/**
 * Names the key that paid for a call. A platform-key source spends credits
 * (accent); every other source does not (muted), which is what makes a zero
 * in the Charged column read as expected rather than as a bug.
 */
function SourceBadge({ sourceKey, label }: { sourceKey?: string; label?: string }) {
  if (!label) return <span style={{ color: 'var(--color-text-secondary)' }}>-</span>
  const billable = sourceKey === 'platform_user' || sourceKey === 'platform_org'
  return (
    <span
      title={billable
        ? 'Served by the platform key — charged to your credit balance.'
        : 'Not charged to your credit balance; the provider bills the key\u2019s owner directly.'}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '1px 7px',
        borderRadius: 999,
        fontSize: '0.8125rem',
        whiteSpace: 'nowrap',
        border: '1px solid var(--color-border)',
        color: billable ? 'var(--color-accent)' : 'var(--color-text-secondary)',
      }}
    >
      <span className="material-icons" style={{ fontSize: 13 }}>
        {billable ? 'account_balance_wallet' : 'key'}
      </span>
      {label}
    </span>
  )
}

/* ── Multi-Select Dropdown ────────────────────────────────────────── */

function MultiSelect({ label, options, selected, onChange }: {
  label: string
  options: string[]
  selected: string[]
  onChange: (val: string[]) => void
}) {
  const [open, setOpen] = useState(false)

  const toggle = (val: string) => {
    onChange(selected.includes(val) ? selected.filter((v) => v !== val) : [...selected, val])
  }

  if (options.length === 0) {
    return (
      <div>
        <label style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', display: 'block', marginBottom: 3 }}>{label}</label>
        <div style={{
          padding: '5px 8px',
          fontSize: '0.8125rem',
          color: 'var(--color-text-secondary)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-md)',
          background: 'var(--color-bg-primary)',
          opacity: 0.6,
        }}>
          No options
        </div>
      </div>
    )
  }

  return (
    <div style={{ position: 'relative' }}>
      <label style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', display: 'block', marginBottom: 3 }}>{label}</label>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        style={{
          width: '100%',
          padding: '5px 8px',
          fontSize: '0.8125rem',
          textAlign: 'left',
          background: 'var(--color-bg-primary)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-md)',
          color: selected.length ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 4,
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selected.length === 0 ? `All ${label.toLowerCase()}s` : selected.length === 1 ? selected[0] : `${selected.length} selected`}
        </span>
        <span className="material-icons" style={{ fontSize: 17, flexShrink: 0 }}>{open ? 'expand_less' : 'expand_more'}</span>
      </button>

      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setOpen(false)} />
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 100,
            marginTop: 2,
            background: 'var(--color-bg-secondary)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            maxHeight: 200,
            overflow: 'auto',
          }}>
            {selected.length > 0 && (
              <button
                type="button"
                onClick={() => { onChange([]); setOpen(false) }}
                style={{
                  width: '100%',
                  padding: '6px 10px',
                  fontSize: '0.8125rem',
                  textAlign: 'left',
                  background: 'none',
                  border: 'none',
                  borderBottom: '1px solid var(--color-border)',
                  color: 'var(--color-accent)',
                  cursor: 'pointer',
                }}
              >
                Clear selection
              </button>
            )}
            {options.map((opt) => (
              <label
                key={opt}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '6px 10px',
                  fontSize: '0.8125rem',
                  cursor: 'pointer',
                  background: selected.includes(opt) ? 'var(--color-bg-hover)' : 'transparent',
                }}
              >
                <input
                  type="checkbox"
                  checked={selected.includes(opt)}
                  onChange={() => toggle(opt)}
                  style={{ accentColor: 'var(--color-accent)' }}
                />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{opt}</span>
              </label>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

/* ── Usage Meter ───────────────────────────────────────────────────── */

function UsageMeter({ label, value, limit, pct, format }: {
  label: string; value: number; limit: number; pct: number; format: (n: number) => string
}) {
  const color = pct >= 90 ? '#f44336' : pct >= 70 ? '#ff9800' : 'var(--color-accent)'
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8125rem', marginBottom: 4 }}>
        <span style={{ fontWeight: 600 }}>{label}</span>
        <span style={{ color: 'var(--color-text-secondary)' }}>{format(value)} / {format(limit)}</span>
      </div>
      <div style={{ height: 8, background: 'var(--color-border)', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{
          height: '100%', borderRadius: 4,
          background: color,
          width: `${pct}%`,
          transition: 'width 500ms ease',
        }} />
      </div>
      <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 2, textAlign: 'right' }}>
        {pct.toFixed(0)}%
      </div>
    </div>
  )
}

/* ── Daily Chart (simple bar chart) ────────────────────────────────── */

function DailyChart({ data }: { data: Array<{ date: string; credits: number; calls: number }> }) {
  const maxCredits = Math.max(...data.map((d) => d.credits), 0.001)
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 120 }}>
      {data.map((d) => {
        const h = Math.max((d.credits / maxCredits) * 100, 2)
        const dateStr = d.date.slice(5) // MM-DD
        return (
          <div
            key={d.date}
            title={`${d.date}: ${formatCredits(d.credits)} credits / ${d.calls} calls`}
            style={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <div style={{
              width: '100%',
              maxWidth: 24,
              height: `${h}%`,
              background: 'var(--color-accent)',
              borderRadius: '2px 2px 0 0',
              opacity: 0.8,
              transition: 'height 300ms ease',
            }} />
            <span style={{
              fontSize: '0.8125rem',
              color: 'var(--color-text-secondary)',
              transform: 'rotate(-45deg)',
              transformOrigin: 'center',
              whiteSpace: 'nowrap',
            }}>
              {dateStr}
            </span>
          </div>
        )
      })}
    </div>
  )
}
